import { InjectionToken } from '@angular/core';
import { environment } from '@ups/xplat/core';
import {
  describeRynReplayListFailure,
  describeRynReplayListNoToken,
} from './ryn-replay-read-failure';
import { RynReplayAccessToken } from './ryn-replay-access-token';
import {
  IRynReplaySessionFilters,
  RynReplayListError,
} from './ryn-replay-session-source';

/**
 * One grouped error row from `GET /api/issues` (#31328).
 */
export interface IRynReplayGroupedIssue {
  signature: string;
  kind: string;
  title: string;
  app: string;
  occurrenceCount: number;
  sessionCount: number;
  /** ISO 8601 UTC. */
  firstSeenAt: string;
  /** ISO 8601 UTC. */
  lastSeenAt: string;
  sampleSessionId: string;
  sampleOffsetMs: number;
}

/**
 * The same filters as the session list (Mel 10/6: Sessions, Issues and Heat map
 * filter alike). `/issues` parses this set server-side; it scans error sessions
 * itself, so `take` and `hasErrors` are not sent.
 */
export type IRynReplayGroupedIssueFilters = Omit<
  IRynReplaySessionFilters,
  'take' | 'hasErrors'
>;

const ISSUE_FILTER_KEYS: ReadonlyArray<keyof IRynReplayGroupedIssueFilters> = [
  'app',
  'from',
  'to',
  'user',
  'userSearch',
  'landingUrl',
  'visitedUrl',
  'appVersion',
  'environment',
  'browser',
  'consoleLog',
];

export interface IRynReplayGroupedIssueListResult {
  issues: IRynReplayGroupedIssue[];
  sessionsScanned: number;
  truncated: boolean;
}

export interface IRynReplayIssueSource {
  list(
    filters?: IRynReplayGroupedIssueFilters
  ): Promise<IRynReplayGroupedIssueListResult>;
}

export const RYN_REPLAY_ISSUE_SOURCE =
  new InjectionToken<IRynReplayIssueSource>('RYN_REPLAY_ISSUE_SOURCE');

/**
 * Reads `GET {readApiUrl}/issues` (#31328).
 */
export class RynReplayHttpIssueSource implements IRynReplayIssueSource {
  constructor(
    private readonly accessToken: () => Promise<RynReplayAccessToken>,
    private readonly fetchRef: typeof fetch = globalThis.fetch?.bind(globalThis)
  ) {}

  async list(
    filters: IRynReplayGroupedIssueFilters = {}
  ): Promise<IRynReplayGroupedIssueListResult> {
    const base = environment.rynReplay?.readApiUrl?.replace(/\/+$/, '');
    if (!base) {
      throw new RynReplayListError(
        'The replay read API is not configured for this app, so grouped issues cannot be listed.'
      );
    }

    // No token is three states, not one (#30126): nobody signed in, a session on
    // an issuer the read API rejects (Auth0), or a failed silent acquisition. The
    // factory now says which, so the grouped-issues list words its refusal from
    // the reason exactly as the session list does, instead of asserting a single
    // "not signed in" for a viewer signed in with the wrong issuer. Every no-token
    // case is one a corporate Entra sign-in can fix, so the kind stays
    // entra-sign-in-required (#31381); only the wording varies by reason.
    const access = await this.accessToken();
    if (!access.token) {
      throw new RynReplayListError(
        describeRynReplayListNoToken(access.reason),
        'entra-sign-in-required'
      );
    }
    const token = access.token;

    const query = new URLSearchParams();
    for (const key of ISSUE_FILTER_KEYS) {
      const value = filters[key]?.trim();
      if (value) {
        query.set(key, value);
      }
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';

    const response = await this.fetchRef(`${base}/issues${suffix}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const message = await readRynReplayIssueListError(response);
      throw new RynReplayListError(message);
    }

    const body = await response.json();
    const rows = body?.issues;
    if (!Array.isArray(rows)) {
      throw new RynReplayListError(
        'The replay service returned a response this app could not read.'
      );
    }

    return {
      issues: rows as IRynReplayGroupedIssue[],
      sessionsScanned:
        typeof body?.sessionsScanned === 'number' ? body.sessionsScanned : 0,
      truncated: body?.truncated === true,
    };
  }
}

const readRynReplayIssueListError = async (
  response: Response
): Promise<string> => {
  if (
    response.status === 401 ||
    response.status === 403 ||
    response.status === 503
  ) {
    return describeRynReplayListFailure(response.status);
  }
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body?.error === 'string' && body.error.trim()) {
      return body.error;
    }
  } catch {
    // Body was not JSON.
  }
  return describeRynReplayListFailure(response.status);
};
